export interface AppConfig {
  API_BASE_URL: string;
  PUBLIC_API_BASE_URL: string;
  STORAGE_URL: string;
  STORAGE_BUCKET: string;
  MODE: string;
  IS_DEV: boolean;
  IS_PROD: boolean;
  SHOW_QUEUE: boolean;
  // The signed-in API (/api) for the staff tools that must not ride on the
  // unauthenticated /apiv2: the Referrals review queue approves money.
  STAFF_API_BASE_URL: string;
  AUTH0_DOMAIN: string;
  AUTH0_CLIENT_ID: string;
  AUTH0_AUDIENCE: string;
}

class EnvironmentConfig {
  private static instance: EnvironmentConfig;
  private config: AppConfig;

  private constructor() {
    const apiUrl = import.meta.env.VITE_DATASETS_API_BASE_URL;
    const storageUrl = import.meta.env.VITE_STORAGE_URL;
    const storageBucket = import.meta.env.VITE_STORAGE_BUCKET;
    const mode = import.meta.env.MODE || 'development';
    // Feature flag: the review Queue only shows in local development. Staging and
    // prod build with `vite build` (mode === 'production'), so it stays hidden
    // there. Set VITE_SHOW_QUEUE=true to force it on in any environment.
    const showQueue = mode === 'development' || import.meta.env.VITE_SHOW_QUEUE === 'true';

    if (!apiUrl) {
      throw new Error('Missing VITE_DATASETS_API_BASE_URL environment variable');
    }
    if (!storageUrl) {
      throw new Error('Missing VITE_STORAGE_URL environment variable');
    }
    if (!storageBucket) {
      throw new Error('Missing VITE_STORAGE_BUCKET environment variable');
    }

    this.config = {
      API_BASE_URL: apiUrl,
      PUBLIC_API_BASE_URL: apiUrl,
      STORAGE_URL: storageUrl,
      STORAGE_BUCKET: storageBucket,
      MODE: mode,
      IS_DEV: mode === 'development',
      IS_PROD: mode === 'production',
      SHOW_QUEUE: showQueue,
      STAFF_API_BASE_URL: import.meta.env.VITE_STAFF_API_BASE_URL || apiUrl.replace(/\/apiv2\/?$/, '/api'),
      AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN || '',
      AUTH0_CLIENT_ID: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
      AUTH0_AUDIENCE: import.meta.env.VITE_AUTH0_AUDIENCE || '',
    };
  }

  static getInstance(): EnvironmentConfig {
    if (!this.instance) {
      this.instance = new EnvironmentConfig();
    }
    return this.instance;
  }

  get<K extends keyof AppConfig>(key: K): AppConfig[K] {
    return this.config[key];
  }
}

export const env = EnvironmentConfig.getInstance();

export const API_BASE_URL = env.get('API_BASE_URL');
export const PUBLIC_API_BASE_URL = env.get('PUBLIC_API_BASE_URL');
export const STORAGE_URL = env.get('STORAGE_URL');
export const STORAGE_BUCKET = env.get('STORAGE_BUCKET');
export const MODE = env.get('MODE');
export const IS_DEV = env.get('IS_DEV');
export const IS_PROD = env.get('IS_PROD');
export const SHOW_QUEUE = env.get('SHOW_QUEUE');